import { RefObject, useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  readonly component: () => JSX.Element;
  readonly scrollableRef: RefObject<HTMLDivElement>;
}

export default function VirtualRow({
  component: Component,
  scrollableRef,
}: Props) {
  const { isVisible, wrapperRef } = useVirtualRowVisibility(scrollableRef);
  const { height, rowContentRef } = useVirtualRowHeight(isVisible);

  const row = useMemo(
    () => (
      <div ref={rowContentRef}>
        <Component />
      </div>
    ),
    [Component, rowContentRef],
  );

  const invisibleRow = useMemo(
    () => (
      <div
        css={`
          height: ${height.toFixed(1)}px;
        `}
      />
    ),
    [height],
  );

  return <div ref={wrapperRef}>{isVisible ? row : invisibleRow}</div>;
}

// (PTP-6870) avoid rendering rows with zero height
const INITIAL_ROW_HEIGHT = 48;

function useVirtualRowHeight(isVisible: boolean) {
  const rowContentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(INITIAL_ROW_HEIGHT);

  useEffect(() => {
    if (!isVisible) return;
    if (rowContentRef.current == null) return;
    const rect = rowContentRef.current.getBoundingClientRect();
    setHeight(rect.height);
  }, [rowContentRef, isVisible]);

  return { rowContentRef, height };
}

function useVirtualRowVisibility(scrollableRef: RefObject<HTMLDivElement>) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const element = wrapperRef.current;
    if (element == null) return;
    const scrollable = scrollableRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        // We'll receive 2 entries when the item is re-rendering, so
        // we must ensure that at least one entry is intersecting.
        const isIntersecting = entries.some((e) => e.isIntersecting);
        setVisible(isIntersecting);
      },
      {
        root: scrollable,
        rootMargin: `${ROOT_MARGIN}px 0px ${ROOT_MARGIN}px 0px`,
      },
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [scrollableRef, setVisible]);

  return {
    wrapperRef,
    isVisible,
  };
}

const ROOT_MARGIN = '400'; // px
